import { ThunkAction } from 'redux-thunk';
import { RootState } from '../..';
import { AnyAction } from 'redux';
import * as microsoftTeams from '@microsoft/teams-js';
import {
    ChatMember,
    isGroupChat,
    isPreviewChat,
    msGraphApi,
} from '../../../services/microsoft/msGraphApi';
import {
    setChatMembers,
    setIsExternalMember,
    setMemberResolution,
    setShareContext,
    setTeamMember,
    setTeamsContext,
} from './actionCreators';
import { OperationId, startFetch, stopFetch } from '../app/actionCreators';
import { batch } from 'react-redux';
import { selectStubUrl } from '../../selectors/app';
import { selectUser } from '../../selectors/auth';
import {
    MemberResolution,
    MemberResolutionStatus,
    ShareContext,
} from '../../reducers/conversation/reducer';
import {
    selectChatMembers,
    selectMemberResolution,
    selectTeamsContext,
} from '../../selectors/conversation';
import { searchUserIdsThunk } from '../app/thunks';
import { AppErrorCode, NotificationStatus } from '../app/actionTypes';
import { selectTranslate } from '../../selectors/ui';
import { setMsalAuthResult } from '../msal/actions';
import { exchangeTeamsToken } from '../../../services/microsoft/msToken';
import { logger } from '../../../helpers/logger';
import { resolveChatMembers } from '../../../services/memberResolution';

export const getConversationContextThunk = (): ThunkAction<
    void,
    RootState,
    unknown,
    AnyAction
> =>
    async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !stubUrl) {
            dispatch(setShareContext(ShareContext.Any));
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.LoadConversationContext,
                message: 'Loading conversation context...',
            }),
        );

        let teamsToken;
        try {
            teamsToken = await microsoftTeams.authentication.getAuthToken();
        } catch (err) {
            logger.error(
                `getAuthToken error: ${
                    err instanceof Error ? err.message : 'unknown'
                }`,
                'Teams',
            );
            dispatch(setShareContext(ShareContext.Any));
            dispatch(
                stopFetch({
                    operationId: OperationId.LoadConversationContext,
                }),
            );
            return;
        }

        const [msAuthRes, teamsContext] = await Promise.all([
            exchangeTeamsToken(teamsToken),
            microsoftTeams.app.getContext(),
        ]);

        dispatch(setMsalAuthResult(msAuthRes));
        dispatch(setTeamsContext(teamsContext));

        const isNotLoggedIn = !msAuthRes;
        if (isNotLoggedIn) {
            batch(() => {
                dispatch(setShareContext(ShareContext.Any));
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadConversationContext,
                    }),
                );
            });
            return;
        }

        let chatMembers: ChatMember[] = [];
        if (teamsContext.chat) {
            const chatMembersRes = await msGraphApi.getChatMembers(
                teamsContext.chat.id,
            );
            chatMembers = chatMembersRes?.value ?? [];
            if (chatMembersRes) {
                dispatch(setChatMembers({ chatMembers }));
            }
        }

        const isChannel = Boolean(teamsContext.channel);
        const isChatInPreview =
            teamsContext.chat !== undefined &&
            isPreviewChat(teamsContext.chat.id);
        const isUnknownCase = !teamsContext.chat;

        // Display user friendly error when trying to use the addin on an empty chat
        if (isChatInPreview) {
            batch(() => {
                dispatch(setShareContext(ShareContext.Any));
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadConversationContext,
                        status: NotificationStatus.Warning,
                        message: translate('chat_not_initialized'),
                    }),
                );
            });
            return;
        }

        if (isChannel || isUnknownCase) {
            batch(() => {
                dispatch(setShareContext(ShareContext.Any));
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadConversationContext,
                    }),
                );
            });
            return;
        }

        if (isGroupChat(chatMembers.length)) {
            batch(() => {
                dispatch(setShareContext(ShareContext.Organization));
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadConversationContext,
                    }),
                );
            });
            return;
        }

        const chatMember = chatMembers.find(
            (member) => member.userId !== teamsContext?.user?.id,
        );
        const userIdsRes = await dispatch(
            searchUserIdsThunk(chatMember?.email ?? ''),
        );

        if (!userIdsRes.success) {
            batch(() => {
                dispatch(setShareContext(ShareContext.Any));
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadConversationContext,
                        errorCode: AppErrorCode.CustomError,
                        message: translate('something_went_wrong'),
                    }),
                );
            });
            return;
        }

        const nextcloudAccountExists = userIdsRes.result.ocs.data.length > 0;

        const shareContext = nextcloudAccountExists
            ? ShareContext.Organization
            : ShareContext.Public;

        batch(() => {
            dispatch(setShareContext(shareContext));
            dispatch(setIsExternalMember(!nextcloudAccountExists));
            dispatch(
                stopFetch({
                    operationId: OperationId.LoadConversationContext,
                }),
            );
        });
    };

export const getTeamsContextThunk = (): ThunkAction<
    void,
    RootState,
    unknown,
    AnyAction
> =>
    async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const stubUrl = selectStubUrl(state);
        if (!user || !stubUrl) {
            dispatch(setShareContext(ShareContext.Any));
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.LoadTeamMemberContext,
                message: 'Loading team member context...',
            }),
        );

        let teamsToken;
        try {
            teamsToken = await microsoftTeams.authentication.getAuthToken();
        } catch (err) {
            logger.error(
                `getAuthToken error: ${
                    err instanceof Error ? err.message : 'unknown'
                }`,
                'Teams',
            );
            dispatch(setShareContext(ShareContext.Any));
            dispatch(
                stopFetch({
                    operationId: OperationId.LoadTeamMemberContext,
                }),
            );
            return;
        }

        const [msAuthRes, teamsContext] = await Promise.all([
            exchangeTeamsToken(teamsToken),
            microsoftTeams.app.getContext(),
        ]);

        dispatch(setMsalAuthResult(msAuthRes));
        dispatch(setTeamsContext(teamsContext));

        const isTeamOrChannel =
            Boolean(teamsContext.team) || Boolean(teamsContext.channel);

        if (isTeamOrChannel) {
            const teamMember = await msGraphApi.getTeamMember(
                teamsContext.team?.groupId ?? '',
                teamsContext.user?.id ?? '',
            );
            if (teamMember) {
                dispatch(setTeamMember(teamMember));
            }
        }

        batch(() => {
            dispatch(
                stopFetch({
                    operationId: OperationId.LoadTeamMemberContext,
                }),
            );
        });
    };

export const resolveChatMembersThunk = (): ThunkAction<
    Promise<MemberResolution>,
    RootState,
    unknown,
    AnyAction
> =>
    async function thunk(dispatch, getState): Promise<MemberResolution> {
        const state = getState();
        const user = selectUser(state);
        const stubUrl = selectStubUrl(state);
        const teamsContext = selectTeamsContext(state);
        const chatMembers = selectChatMembers(state);
        const memberResolution = selectMemberResolution(state);

        if (
            !user ||
            !stubUrl ||
            memberResolution.status !== MemberResolutionStatus.Idle
        ) {
            return memberResolution;
        }

        const candidates = chatMembers.filter(
            (member) =>
                member.userId !== teamsContext?.user?.id &&
                Boolean(member.email),
        );

        dispatch(
            setMemberResolution({
                status: MemberResolutionStatus.Loading,
                members: [],
            }),
        );

        const members = await resolveChatMembers({ stubUrl, user }, candidates);

        const resolution: MemberResolution = {
            status: MemberResolutionStatus.Done,
            members,
        };

        dispatch(setMemberResolution(resolution));

        return resolution;
    };
